import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle2, XCircle } from 'lucide-react';
import api, { formatApiError } from '../lib/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('form');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setStatus('success');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center bg-page-bg px-4">
      <div className="glass-panel p-8 text-center max-w-md">
        <XCircle className="w-12 h-12 text-status-red mx-auto mb-3" strokeWidth={1.5} />
        <h2 className="font-display text-xl font-extrabold text-ink mb-2">Invalid Reset Link</h2>
        <p className="font-body text-sm text-ink/50 mb-4">This password reset link is invalid or has expired.</p>
        <Link to="/forgot-password" className="font-body text-sm text-brand-orange underline">Request a new reset link</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-page-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-10 h-10 rounded-[4px] bg-brand-orange flex items-center justify-center mx-auto mb-3">
            <span className="font-display text-white text-lg font-extrabold">S</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink">New Password</h1>
        </div>

        <div className="glass-panel p-6" data-testid="reset-password-card">
          {status === 'success' ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-status-green mx-auto mb-3" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-extrabold text-ink mb-2">Password Reset</h2>
              <p className="font-body text-sm text-ink/50 mb-4">Your password has been updated successfully.</p>
              <Button onClick={() => navigate('/login')} className="bg-brand-orange text-white hover:bg-brand-orange/90 font-body rounded-[4px]" data-testid="reset-continue">
                Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="font-body text-sm text-ink/50">Choose a strong password. Must be 8+ characters with uppercase, lowercase, digit, and special character.</p>
              {error && <div className="bg-status-red/10 border border-status-red/20 text-status-red px-3 py-2 rounded-[4px] text-xs font-body">{error}</div>}
              <div>
                <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">New Password</Label>
                <Input data-testid="reset-password" type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" className="mt-1 font-body text-sm" />
              </div>
              <div>
                <Label className="font-body text-xs uppercase tracking-[0.1em] text-ink/70">Confirm Password</Label>
                <Input data-testid="reset-confirm" type="password" required value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" className="mt-1 font-body text-sm" />
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full bg-brand-orange text-white hover:bg-brand-orange/90 font-body font-medium rounded-[4px]">
                <Lock className="w-4 h-4 mr-2" /> {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
